import { createPopper } from "@popperjs/core";
import findAndReplaceDOMText from "findandreplacedomtext";

const quoteattr = function (s, preserveCR) {
  preserveCR = preserveCR ? "&#13;" : "\n";
  return (
    ("" + s) /* Forces the conversion to string. */
      .replace(/&/g, "&amp;") /* This MUST be the 1st replacement. */
      .replace(/'/g, "&apos;") /* The 4 other predefined entities, required. */
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      /*
      You may add other replacements here for HTML only
      (but it's not necessary).
      Or for XML, only if the named entities are defined in its DTD.
      */
      .replace(/\r\n/g, preserveCR) /* Must be before the next replacement. */
      .replace(/[\r\n]/g, preserveCR)
  );
};

const Glossary = () => {
  // convert the first matching text string within a marked data-glossary area
  // with a rich button element allowing the user to open a popover with the
  // glossary definition
  const glossary = document.querySelectorAll("[data-glossary]");
  const terms = window.GLOSSARY;
  let focusTermOnClose = true;

  // detecting if the browser has "touch capabilities"
  const isTouchEnabled = () => {
    return (
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
  };

  if (typeof terms === "undefined") {
    return;
  }

  let openButton = null;

  // if esc key is pressed, close the popup and remove the active class from the button
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      if (openButton) {
        openButton.classList.remove("glossary-button--active");

        if (focusTermOnClose) openButton.focus();

        openButton
          .querySelector('[role="tooltip"]')
          .setAttribute("tabindex", "-1");
      }
    }
  });

  // check for a match of title in the innerHTML. title must be followed by a space
  const augmentTextWithGlossary = (item, term, termIdx, id) => {
    const { title, subtitle, content } = term;
    const trimmedTitle = title.trim();
    const re = `${trimmedTitle}[ ,.]`;

    // break it down to tokens
    findAndReplaceDOMText(item, {
      find: new RegExp(re, "i"),
      filterElements: (el) => {
        if (
          el.tagName === "BUTTON" ||
          el.tagName === "A" ||
          el.tagName === "U"
        ) {
          return false;
        }

        if (el.getAttribute("class")?.indexOf("glossary") > -1) {
          return false;
        }

        return true;
      },
      replace: function (portion, match) {
        // check to see if this term has already been handled
        const alreadyHandled = document.querySelector(
          '[aria-describedby="glossary-tooltip-' + id + '"]'
        );

        if (alreadyHandled) {
          return portion.text;
        }

        // if the match includes a space, period of comma take it off the match and store as a character to be
        // readded later
        const lastChar = match[0].slice(-1);
        if (lastChar === " " || lastChar === "." || lastChar === ",") {
          match[0] = match[0].slice(0, -1);
        }

        // replace the matched text with a button
        const button = document.createElement("button");
        button.setAttribute("type", "button");
        button.setAttribute("data-glossary-ignore", "true");
        button.setAttribute("aria-describedby", "glossary-tooltip-" + id);
        button.setAttribute(
          "title",
          "Open glossary definition for " + quoteattr(match)
        );
        button.classList.add("glossary-button");
        button.setAttribute("data-glossary-button", title);
        button.innerHTML = match;

        const tooltip = document.createElement("div");
        tooltip.setAttribute("role", "tooltip");
        tooltip.setAttribute("tabindex", "-1");
        tooltip.classList.add("glossary-tooltip");
        tooltip.setAttribute("id", "glossary-tooltip-" + id);
        tooltip.innerHTML =
          `
          <div data-popper-arrow></div>
          <div class="glossary-tooltip__content">
            <h3 class="glossary-tooltip__title">` +
          title +
          `</h3>
            <p class="glossary-tooltip__subtitle">` +
          subtitle +
          `</h3>
            <p class="glossary-tooltip__inner"><strong>This means:</strong>` +
          content +
          `</p>
            <a class="glossary-tooltip__close" title="Close tooltip">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" focusable="false">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.27029 16.375C5.90981 16.7468 5.90981 17.3495 6.2703 17.7212C6.63078 18.0929 7.21524 18.0929 7.57573 17.7212L12 13.1589L16.4243 17.7212C16.7848 18.0929 17.3692 18.0929 17.7297 17.7212C18.0902 17.3495 18.0902 16.7468 17.7297 16.375L13.3054 11.8127L17.3665 7.62496C17.727 7.25323 17.727 6.65053 17.3665 6.2788C17.006 5.90706 16.4215 5.90706 16.061 6.2788L12 10.4666L7.93895 6.2788C7.57847 5.90706 6.994 5.90706 6.63352 6.2788C6.27303 6.65053 6.27303 7.25323 6.63352 7.62496L10.6946 11.8127L6.27029 16.375Z" fill="#2F3A49"/>
            </svg>
            </a>
            </div>
          </div>`;
        button.appendChild(tooltip);

        const span = document.createElement("span");

        span.appendChild(button);

        // if lastChar then add it back after the button
        if (lastChar === " " || lastChar === "." || lastChar === ",") {
          span.innerHTML += lastChar;
        }

        // append the content to the bottom of the <main> tag for print
        let printAppendix = document.querySelector(".print-appendix");
        if (!printAppendix) {
          let printAppendixCreated = document.createElement("div");
          printAppendixCreated.classList.add("print-appendix");

          // add a title to the appendix
          const printAppendixTitle = document.createElement("h2");
          printAppendixTitle.classList.add("print-appendix__title");
          printAppendixTitle.innerHTML = "Glossary Terms";
          printAppendixCreated.appendChild(printAppendixTitle);

          document.querySelector("main").appendChild(printAppendixCreated);

          printAppendix = document.querySelector(".print-appendix");
        }

        // create a div with the title, subtitle and content
        const printContent = document.createElement("div");
        printContent.classList.add("print-appendix__content");
        printContent.setAttribute("id", "print-appendix-" + id);
        printContent.innerHTML =
          `<h5 class="print-appendix__content-title">` +
          title +
          `</h5>
          <p class="print-appendix__subtitle">` +
          subtitle +
          `</p>
          <p class="print-appendix__inner"><strong>This means:</strong> ` +
          content +
          `</p>`;

        printAppendix?.appendChild(printContent);

        return span;
      },
    });
  };

  glossary.forEach((el) => {
    terms.forEach((term, termIdx) => {
      const { title } = term;

      // convert title to a id safe attribute
      const id = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

      // that are not marked with data-glossary-ignore
      el.querySelectorAll("p, li").forEach((item) => {
        if (
          item.dataset.glossaryIgnore &&
          item.dataset.glossaryIgnore === "true"
        ) {
          return false;
        }

        // cannot highlight within a link
        if (item.closest("a")) {
          return false;
        }

        if (item.closest("button")) {
          return false;
        }

        if (item.closest("[data-glossary-ignore]")) {
          return false;
        }

        augmentTextWithGlossary(item, term, termIdx, id);
      });
    });
  });

  // add event listener to all glossary buttons
  const buttons = document.querySelectorAll("[data-glossary-button]");
  buttons.forEach((button) => {
    if (button.classList.contains("attached")) {
      return;
    }

    button.classList.add("attached");

    // on mouse over button, trigger a click if it's not already active for non touch devices
    if (!isTouchEnabled()) {
      button.addEventListener("mouseover", (e) => {
        // timeout to handle jump/skip links from triggering if cursor over
        setTimeout(() => {
          if (!button.classList.contains("glossary-button--active")) {
            button.click();
          }
        }, 150);
      });

      // on mouse out, close the tooltip for non touch devices
      button.addEventListener("mouseout", (e) => {
        setTimeout(() => {
          if (button.classList.contains("glossary-button--active")) {
            button.click();
          }
        }, 150);
      });
    }

    button.addEventListener("click", (e) => {
      e.preventDefault();

      let open = !button.classList.contains("glossary-button--active");

      if (openButton) {
        openButton.classList.remove("glossary-button--active");
        openButton
          .querySelector(".glossary-tooltip")
          .setAttribute("tabindex", -1);
        openButton = null;
      }

      if (open) {
        button.classList.add("glossary-button--active");
        const tooltip = button.querySelector(".glossary-tooltip");
        button
          .querySelector(".glossary-tooltip")
          ?.setAttribute("tabindex", "0");

        if (tooltip) {
          openButton = button;
          createPopper(button, tooltip, {
            modifiers: [
              {
                name: "arrow",
                enabled: true,
                phase: "main",
                options: {
                  padding: 5,
                  enabled: true,
                  element: tooltip.querySelector("[data-popper-arrow]"),
                },
              },
            ],
          });
        }
      }
    });

    // if clicking anywhere but the button or tooltip, close the tooltip
    document.addEventListener("click", (e) => {
      if (!e.target) {
        return;
      }
      if (
        e.target.classList.contains("glossary-tooltip__close") ||
        !button.contains(e.target)
      ) {
        button.classList.remove("glossary-button--active");
        button.querySelector(".glossary-tooltip").setAttribute("tabindex", -1);
      }
    });
  });
};

export default Glossary;
